import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";

import {
  fetchAnnouncement,
  updateAdminAnnouncement,
  deleteAdminAnnouncement,
  fetchAdminAnnouncementStats,
  AdminAnnouncement,
  AnnouncementStats,
} from "../api";
import AdminAnnouncementForm from "../components/Admin/AdminAnnouncementForm";
import PageHero from "../components/common/PageHero";
import { useSnackbarStore } from "../store/useSnackbarStore";

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminAnnouncementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const [announcement, setAnnouncement] = React.useState<AdminAnnouncement | null>(null);
  const [stats, setStats] = React.useState<AnnouncementStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!id) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [announcementData, statsData] = await Promise.all([
          fetchAnnouncement(id),
          fetchAdminAnnouncementStats(id).catch(() => null),
        ]);

        if (!active) return;

        setAnnouncement((announcementData as AdminAnnouncement) ?? null);
        setStats((statsData as AnnouncementStats) ?? null);
      } catch (err) {
        console.error("Failed to load announcement", err);
        if (active) {
          setError("We were unable to load this announcement. Please try again shortly.");
          setAnnouncement(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [id]);

  const handleUpdate = async (data: Pick<AdminAnnouncement, "title" | "content">) => {
    if (!id) return;
    try {
      setSaving(true);
      await updateAdminAnnouncement(id, data);
      const refreshed = await fetchAnnouncement(id);
      setAnnouncement(refreshed as AdminAnnouncement);
      showSnackbar("Announcement updated successfully.", "success");
    } catch (err) {
      console.error("Failed to update announcement", err);
      showSnackbar("Unable to update the announcement. Please retry.", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || deleting) return;
    const confirmed = window.confirm("Are you sure you want to delete this announcement?");
    if (!confirmed) return;

    try {
      setDeleting(true);
      await deleteAdminAnnouncement(id);
      showSnackbar("Announcement removed.", "success");
      navigate("/announcements");
    } catch (err) {
      console.error("Failed to delete announcement", err);
      showSnackbar("Unable to delete this announcement. Please try again.", "danger");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ pb: 2 }}>
      <PageHero
        eyebrow="Inspire seva"
        title={announcement ? announcement.title : loading ? "Loading announcement" : "Announcement not found"}
        description="Edit the message, celebrate devotees who engaged, and steward future communications with confidence."
        actions={[
          <Button
            key="back"
            variant="plain"
            color="neutral"
            startDecorator={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/announcements")}
          >
            Back to announcements
          </Button>,
          <Button
            key="delete"
            variant="outlined"
            color="danger"
            startDecorator={<DeleteOutlineRoundedIcon />}
            onClick={handleDelete}
            disabled={!announcement || deleting}
            data-testid="delete-announcement"
          >
            Delete announcement
          </Button>,
        ]}
      />

      {error ? (
        <Alert
          color="danger"
          variant="soft"
          sx={{ borderRadius: "lg" }}
          endDecorator={
            <Button size="sm" variant="plain" color="danger" onClick={() => navigate("/announcements")}>
              Back to announcements
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      {(!announcement && !loading && !error) ? (
        <Card
          variant="outlined"
          sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, textAlign: "center" }}
        >
          <Typography level="title-md" sx={{ fontWeight: 700 }}>
            This announcement could not be found.
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", mt: 1 }}>
            It may have been deleted or you may have followed an outdated link.
          </Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate("/announcements")}>Return to announcements</Button>
        </Card>
      ) : (
        <Grid container spacing={2.5} alignItems="stretch">
          <Grid xs={12} lg={7}>
            {loading ? (
              <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={1.5}>
                  <Skeleton variant="text" level="h2" width="60%" />
                  <Skeleton variant="text" level="body-md" width="90%" />
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: "lg" }} />
                </Stack>
              </Card>
            ) : announcement ? (
              <AdminAnnouncementForm
                initial={announcement}
                onSubmit={handleUpdate}
                submitLabel="Save changes"
                title="Announcement content"
                helperText="Revise details to keep the seva invitation timely and accurate."
                isSubmitting={saving}
              />
            ) : null}
          </Grid>
          <Grid xs={12} lg={5}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: "xl",
                p: { xs: 2.5, md: 3 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={<CampaignRoundedIcon fontSize="small" />}
                  sx={{ fontWeight: 600 }}
                >
                  Engagement insights
                </Chip>
                {stats ? (
                  <Chip size="sm" variant="soft" color="neutral">
                    Read count: {stats.readCount ?? 0}
                  </Chip>
                ) : null}
              </Stack>

              {loading ? (
                <Stack spacing={1}>
                  <Skeleton variant="text" level="body-sm" width="80%" />
                  <Skeleton variant="text" level="body-sm" width="60%" />
                </Stack>
              ) : announcement ? (
                <Stack spacing={1.25}>
                  <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    Track how many devotees opened this announcement and when it was last updated to keep communications timely.
                  </Typography>
                  <Divider />
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScheduleRoundedIcon fontSize="small" />
                      <Typography level="body-sm">
                        Published on: {formatDateTime(announcement.created_at)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GroupsRoundedIcon fontSize="small" />
                      <Typography level="body-sm">
                        Devotees reached: {stats?.readCount ?? 0}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              ) : (
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Announcements removed from the library will no longer gather insights.
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
}
